import {Component, inject, OnInit, signal, ViewChild} from '@angular/core';
import {
  ButtonCloseDirective,
  ButtonDirective,
  FormCheckComponent,
  FormCheckInputDirective,
  FormCheckLabelDirective,
  FormControlDirective,
  FormDirective,
  FormFeedbackComponent,
  FormLabelDirective,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterComponent,
  ModalHeaderComponent,
  ModalTitleDirective,
  ModalToggleDirective
} from "@coreui/angular";
import {DialogBaseComponent} from "../../dialogs/dialog-base/dialog-base.component";
import {MarkdownComponent} from "ngx-markdown";
import {MatButton} from "@angular/material/button";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {FrequentlyAskedQuestionsService} from "../../../services/frequently-asked-questions.service";
import {IAddFrequentlyAskedQuestions} from "../../../interfaces/IAddFrequentlyAskedQuestions";
import {SnackbarService} from "../../../services/snackbar.service";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {IApiErrorResponse} from "../../../interfaces/error/IApiErrorResponse";
import {FaqScopeTypes} from "../../../enum/FaqScopeTypes";
import {IFaqAddDialogData} from "../../../interfaces/IFaqAddDialogData";
import {FaqGameSelectorComponent} from "../faq-game-selector/faq-game-selector.component";

@Component({
  selector: 'app-faq-add',
  standalone: true,
  imports: [
    ButtonCloseDirective,
    ButtonDirective,
    DialogBaseComponent,
    FormCheckComponent,
    FormCheckInputDirective,
    FormCheckLabelDirective,
    FormControlDirective,
    FormDirective,
    FormFeedbackComponent,
    FormLabelDirective,
    MarkdownComponent,
    MatButton,
    ModalBodyComponent,
    ModalComponent,
    ModalFooterComponent,
    ModalHeaderComponent,
    ModalTitleDirective,
    ReactiveFormsModule,
    ModalToggleDirective,
    FaqGameSelectorComponent
  ],
  templateUrl: './faq-add.component.html',
  styleUrl: './faq-add.component.css'
})
export class FaqAddComponent implements OnInit {
  private frequentlyAskedQuestionsService = inject(FrequentlyAskedQuestionsService);
  private snackbarService = inject(SnackbarService);
  private matDialogRef = inject(MatDialogRef<FaqAddComponent>);
  private dialogData = inject<IFaqAddDialogData>(MAT_DIALOG_DATA);

  @ViewChild(FaqGameSelectorComponent) gameSelector!: FaqGameSelectorComponent;

  public uiState: UIStateEnum = UIStateEnum.ShowData;
  public uiStateEnumForTemplate = UIStateEnum;
  public answerContentControl = new FormControl<string | null>("", Validators.required);
  public questionControl = new FormControl<string | null>("", Validators.required);
  public activeControl = new FormControl<boolean>(false);
  public scopeTypeControl = new FormControl<number>(1, Validators.required);
  public sharedControl = new FormControl<boolean>(true);

  public addFaqForm = new FormGroup({
    question: this.questionControl,
    answer: this.answerContentControl,
    active: this.activeControl,
    scopeType: this.scopeTypeControl,
    shared: this.sharedControl
  });

  public canEditWebappFaqs: boolean = false;
  public canEditAdminAppFaqs: boolean = false;
  public availableScopes: Array<{ value: number, label: string }> = [];
  public initialSelectedGameIds = signal<string[]>([]);

  ngOnInit() {
    this.canEditWebappFaqs = this.dialogData.canEditWebappFaqs;
    this.canEditAdminAppFaqs = this.dialogData.canEditAdminAppFaqs;

    // Build available scope options based on permissions
    if (this.canEditWebappFaqs) {
      this.availableScopes.push({value: FaqScopeTypes.WebApp, label: 'Web App'});
    }
    if (this.canEditAdminAppFaqs) {
      this.availableScopes.push({value: FaqScopeTypes.AdminApp, label: 'Admin App'});
    }

    // Set scope type - use passed value if available, otherwise default to first available
    if (this.dialogData.selectedAppType !== undefined && this.dialogData.selectedAppType !== null) {
      this.scopeTypeControl.setValue(this.dialogData.selectedAppType);
    } else if (this.availableScopes.length > 0) {
      this.scopeTypeControl.setValue(this.availableScopes[0].value);
    }

    // Set shared/game-specific based on passed value
    if (this.dialogData.isShared !== undefined) {
      this.sharedControl.setValue(this.dialogData.isShared);
    }

    // Set initial selected game if provided and not shared
    if (this.dialogData.selectedGameId && !this.sharedControl.value) {
      this.initialSelectedGameIds.set([this.dialogData.selectedGameId]);
    }
  }

  addFaq() {
    this.addFaqForm.markAllAsTouched();

    if (!this.addFaqForm.valid) {
      return;
    }

    // Get selected game IDs for game-specific FAQs
    const selectedGameIds = this.sharedControl.value ? [] : this.gameSelector?.getSelectedGameIds() ?? [];

    // Validate game selection for game-specific FAQs
    if (!this.sharedControl.value && selectedGameIds.length === 0) {
      this.snackbarService.openErrorSnackBar('Please select at least one game for game-specific FAQs');
      return;
    }

    this.uiState = UIStateEnum.ShowLoading;

    const command: IAddFrequentlyAskedQuestions = {
      active: this.activeControl.value!,
      answer: this.answerContentControl.value!,
      question: this.questionControl.value!,
      ScopeType: this.scopeTypeControl.value!,
      shared: this.sharedControl.value!,
      gameIds: selectedGameIds,
      gameId: this.dialogData.selectedGameId
    };

    const serviceCall = this.scopeTypeControl.value === FaqScopeTypes.WebApp
      ? this.frequentlyAskedQuestionsService.addWebappFaq(command)
      : this.frequentlyAskedQuestionsService.addAdminAppFaq(command);

    serviceCall.subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
        this.snackbarService.openSuccessfulSnackBar('Successfully added FAQ');
        this.matDialogRef.close();
      }, error: (res: IApiErrorResponse) => {
        this.uiState = UIStateEnum.ShowData;
        if (res.Error?.ClientErrorMessages?.length > 0) {
          this.snackbarService.openErrorSnackBar(res.Error.ClientErrorMessages.toString());
        } else {
          this.snackbarService.openErrorSnackBar('There was an issue adding your FAQ');
        }
      }
    })
  }
}
